/**
 * 考勤管理 store 模块
 * 管理端班级概览走 /api/attendance
 * 班级学生明细走 /api/sign-in-management/class/{class_id}/students
 */
import {
  getAttendanceClasses
} from '@/api/attendance'
import { getClassStudentsSignIn } from '@/api/signInManagement'

function getLocalDateString(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function normalizeRate(raw, signedCount, totalStudents) {
  const parsed = Number(raw)
  if (Number.isFinite(parsed)) return parsed
  if (!totalStudents) return 0
  return signedCount / totalStudents
}

function buildPeriodParam(periodConfigId) {
  if (periodConfigId === undefined || periodConfigId === null || periodConfigId === '') return {}
  return { period_config_id: periodConfigId }
}

function normalizeClassList(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map(item => {
    const totalStudents = toNumber(item.total_students ?? item.total, 0)
    const signedCount = toNumber(item.signed_count ?? item.signed, 0)
    const leaveCount = toNumber(item.leave_count ?? item.leave, 0)
    const absentCount = toNumber(item.absent_count ?? item.absent, 0)
    const lateCount = toNumber(item.late_count ?? item.late, 0)
    const unsignedFallback = Math.max(0, (leaveCount + absentCount) || (totalStudents - signedCount))
    const unsignedCount = toNumber(item.unsigned_count ?? item.unsigned, unsignedFallback)
    return {
      class_id: item.class_id ?? item.id,
      class_name: item.class_name ?? item.name ?? '',
      grade: item.grade ?? '',
      total_students: totalStudents,
      signed_count: signedCount,
      unsigned_count: unsignedCount,
      leave_count: leaveCount,
      absent_count: absentCount,
      late_count: lateCount,
      attendance_rate: normalizeRate(item.attendance_rate, signedCount, totalStudents)
    }
  })
}

function resolveClassQueryPayload(payload, state) {
  const today = getLocalDateString()
  const normalizeDate = (value, fallback) => {
    if (value === undefined || value === null) return fallback
    const text = String(value).trim()
    return text || fallback
  }
  const stateQueryDate = normalizeDate(state.queryDate, today)

  if (payload && typeof payload === 'object') {
    return {
      queryDate: normalizeDate(payload.queryDate ?? payload.query_date, stateQueryDate),
      periodConfigId: payload.periodConfigId ?? payload.period_config_id ?? state.periodConfigId,
      pageSize: payload.pageSize ?? payload.page_size,
      gradeId: payload.gradeId ?? payload.grade_id
    }
  }
  return {
    queryDate: normalizeDate(payload, stateQueryDate),
    periodConfigId: state.periodConfigId,
    pageSize: undefined,
    gradeId: undefined
  }
}

function resolveClassRows(data = {}) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data.data)) return data.data
  return data.classes ?? data.class_list ?? data.list ?? data.records ?? []
}

function normalizeClassStudents(data = {}, selectedPeriodConfigId) {
  const signed = Array.isArray(data.signed_students) ? data.signed_students : (Array.isArray(data.signed) ? data.signed : [])
  const leave = Array.isArray(data.leave_students) ? data.leave_students : (Array.isArray(data.leave) ? data.leave : [])
  const absent = Array.isArray(data.absent_students) ? data.absent_students : (Array.isArray(data.absent) ? data.absent : [])
  const unsignedRaw = Array.isArray(data.unsigned_students) ? data.unsigned_students : (Array.isArray(data.unsigned) ? data.unsigned : [])
  const unsigned = unsignedRaw.length ? unsignedRaw : [...absent, ...leave]

  const summaryRaw = data.summary ?? {}
  const signedCount = toNumber(summaryRaw.signed_count, signed.length)
  const leaveCount = toNumber(summaryRaw.leave_count, leave.length)
  const absentCount = toNumber(summaryRaw.absent_count, absent.length)
  const unsignedCount = toNumber(summaryRaw.unsigned_count, leaveCount + absentCount)
  const totalStudents = toNumber(summaryRaw.total_students, signedCount + unsignedCount)
  const attendanceRate = normalizeRate(summaryRaw.attendance_rate, signedCount, totalStudents)

  const periodContext = data.period_context ?? null
  const selectedPeriodId = periodContext?.selected_period_config_id ?? data.selected_period_config_id ?? selectedPeriodConfigId ?? null

  return {
    signed,
    unsigned,
    absent,
    leave,
    summary: {
      ...summaryRaw,
      signed_count: signedCount,
      leave_count: leaveCount,
      absent_count: absentCount,
      unsigned_count: unsignedCount,
      total_students: totalStudents,
      attendance_rate: attendanceRate
    },
    periodContext,
    selectedPeriodConfigId: selectedPeriodId
  }
}

function calcRateFromResponse(data = {}) {
  const list = normalizeClassList(resolveClassRows(data))
  const total = toNumber(data.total, list.reduce((sum, item) => sum + toNumber(item.total_students, 0), 0))
  const signed = toNumber(data.total_signed ?? data.signed, list.reduce((sum, item) => sum + toNumber(item.signed_count, 0), 0))
  if (total > 0) return Math.round((signed / total) * 1000) / 10

  const rawRate = Number(data.attendance_rate)
  if (Number.isFinite(rawRate)) {
    return rawRate <= 1 ? Math.round(rawRate * 1000) / 10 : Math.round(rawRate * 10) / 10
  }
  return 0
}

export default {
  namespaced: true,
  state: {
    classList: [],
    queryDate: '',
    periodConfigId: null,
    weeklyTrend: [],
    classLoading: false,
    classError: '',
    classStudents: {
      signed: [],
      unsigned: [],
      absent: [],
      leave: [],
      summary: null,
      periodContext: null,
      selectedPeriodConfigId: null
    },
    studentsError: '',
    studentsLoading: false
  },
  getters: {
    classList: state => state.classList,
    queryDate: state => state.queryDate,
    periodConfigId: state => state.periodConfigId,
    classLoading: state => state.classLoading,
    classError: state => state.classError,
    classStudents: state => state.classStudents,
    studentsError: state => state.studentsError,
    studentsLoading: state => state.studentsLoading,
    schoolSummary(state) {
      const list = state.classList
      const total = list.reduce((sum, item) => sum + toNumber(item.total_students, 0), 0)
      const signed = list.reduce((sum, item) => sum + toNumber(item.signed_count, 0), 0)
      const unsigned = list.reduce((sum, item) => sum + toNumber(item.unsigned_count, 0), 0)
      const absent = list.reduce((sum, item) => sum + toNumber(item.absent_count, 0), 0)
      const late = list.reduce((sum, item) => sum + toNumber(item.late_count, 0), 0)
      const rate = total > 0 ? Math.round((signed / total) * 1000) / 10 : 0
      return { total, signed, unsigned: Math.max(unsigned, total - signed), absent, late, rate }
    },
    weeklyTrendData(state) {
      return (state.weeklyTrend || []).map(item => item.rate)
    },
    weeklyTrendLabels() {
      return ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    }
  },
  mutations: {
    SET_CLASS_LIST(state, { list, queryDate, periodConfigId }) {
      state.classList = list || []
      if (queryDate !== undefined) state.queryDate = queryDate || ''
      if (periodConfigId !== undefined) state.periodConfigId = periodConfigId === '' ? null : (periodConfigId ?? null)
    },
    SET_PERIOD_CONFIG_ID(state, periodConfigId) {
      state.periodConfigId = periodConfigId === '' ? null : (periodConfigId ?? null)
    },
    SET_WEEKLY_TREND(state, list) {
      state.weeklyTrend = list || []
    },
    SET_CLASS_LOADING(state, value) {
      state.classLoading = !!value
    },
    SET_CLASS_ERROR(state, value) {
      state.classError = String(value || '').trim()
    },
    SET_CLASS_STUDENTS(state, payload = {}) {
      state.classStudents = {
        signed: payload.signed || [],
        unsigned: payload.unsigned || [],
        absent: payload.absent || [],
        leave: payload.leave || [],
        summary: payload.summary || null,
        periodContext: payload.periodContext || null,
        selectedPeriodConfigId: payload.selectedPeriodConfigId ?? null
      }
    },
    SET_STUDENTS_LOADING(state, value) {
      state.studentsLoading = !!value
    },
    SET_STUDENTS_ERROR(state, value) {
      state.studentsError = String(value || '').trim()
    },
    SET_QUERY_DATE(state, value) {
      state.queryDate = value || ''
    }
  },
  actions: {
    async fetchClasses({ commit, state }, payload) {
      commit('SET_CLASS_LOADING', true)
      commit('SET_CLASS_ERROR', '')
      const { queryDate, periodConfigId, pageSize, gradeId } = resolveClassQueryPayload(payload, state)
      const periodParam = buildPeriodParam(periodConfigId)

      try {
        const response = await getAttendanceClasses({
          query_date: queryDate,
          grade_id: gradeId,
          page_size: pageSize,
          ...periodParam
        })

        const data = response?.data ?? {}
        const list = normalizeClassList(resolveClassRows(data))
        const commitQueryDate = data.query_date ?? response?.query_date ?? queryDate
        const commitPeriodConfigId = data.period_config_id ?? response?.period_config_id ?? (periodConfigId ?? null)
        commit('SET_CLASS_LIST', { list, queryDate: commitQueryDate, periodConfigId: commitPeriodConfigId })
        commit('SET_CLASS_ERROR', '')
        return { list, queryDate: commitQueryDate, periodConfigId: commitPeriodConfigId }
      } catch (error) {
        const message = String(error?.message || '考勤概览加载失败，请稍后重试').trim()
        commit('SET_CLASS_ERROR', message)
        return {
          list: state.classList || [],
          queryDate: state.queryDate || queryDate,
          periodConfigId: state.periodConfigId ?? periodConfigId ?? null,
          error: message
        }
      } finally {
        commit('SET_CLASS_LOADING', false)
      }
    },
    async fetchWeeklyTrend({ commit, state }) {
      const getMonday = (date) => {
        const copy = new Date(date)
        const day = copy.getDay()
        const diff = day === 0 ? -6 : 1 - day
        copy.setDate(copy.getDate() + diff)
        return copy
      }

      const monday = getMonday(new Date())
      const today = getLocalDateString()
      const periodParam = buildPeriodParam(state.periodConfigId)
      const days = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday)
        d.setDate(monday.getDate() + i)
        days.push(getLocalDateString(d))
      }

      const trend = []
      for (const date of days) {
        if (date > today) {
          trend.push({ date, rate: 0 })
          continue
        }
        try {
          const response = await getAttendanceClasses({
            query_date: date,
            ...periodParam
          })
          const data = response?.data ?? {}
          trend.push({ date, rate: calcRateFromResponse(data) })
        } catch {
          trend.push({ date, rate: 0 })
        }
      }

      commit('SET_WEEKLY_TREND', trend)
      return trend
    },
    async fetchClassStudents({ commit, state }, payload = {}) {
      const classId = payload.classId ?? payload.class_id
      const queryDate = payload.queryDate ?? payload.query_date ?? state.queryDate ?? getLocalDateString()
      const periodConfigId = payload.periodConfigId ?? payload.period_config_id ?? state.periodConfigId

      if (!classId) {
        const empty = { signed: [], unsigned: [], absent: [], leave: [], summary: null, periodContext: null, selectedPeriodConfigId: null }
        commit('SET_CLASS_STUDENTS', empty)
        commit('SET_STUDENTS_ERROR', '')
        return empty
      }

      commit('SET_STUDENTS_LOADING', true)
      commit('SET_STUDENTS_ERROR', '')
      try {
        const response = await getClassStudentsSignIn(classId, {
          sign_date: queryDate,
          ...buildPeriodParam(periodConfigId)
        })
        const normalized = normalizeClassStudents(response?.data ?? {}, periodConfigId)
        commit('SET_CLASS_STUDENTS', normalized)
        commit('SET_STUDENTS_ERROR', '')
        return normalized
      } catch (error) {
        const message = String(error?.message || '班级学生考勤加载失败，请稍后重试').trim()
        commit('SET_STUDENTS_ERROR', message)
        return {
          ...(state.classStudents || {}),
          error: message,
          selectedPeriodConfigId: state.classStudents?.selectedPeriodConfigId ?? periodConfigId ?? null
        }
      } finally {
        commit('SET_STUDENTS_LOADING', false)
      }
    }
  }
}
